import { alpha } from '@mui/material/styles';
import { AssessmentType } from '../enums/shared.enums';

// ----------------------------------------------------------------------

function createGradient(color1: string, color2: string, angle?: number) {
    return `linear-gradient(${angle ? `${angle}deg` : 'to bottom'}, ${color1}, ${color2})`;
}

export type ColorSchema =
    | 'primary'
    | 'secondary'
    | 'info'
    | 'success'
    | 'warning'
    | 'error'
    | 'tertiary'
    | 'tertiaryLight'
    | 'primaryIndigo'
    | 'tertiaryBlue';

interface GradientsPaletteOptions {
    primary: string;
    info: string;
    success: string;
    warning: string;
    error: string;
    card: string;
}

interface BrandPaletteOptions {
    /** The light blue in the ReachLink mark; co-branded gradients end on it */
    blue: string;
}

interface ChartPaletteOptions {
    violet: string[];
    blue: string[];
    green: string[];
    yellow: string[];
    red: string[];
}

declare module '@mui/material/styles/createPalette' {
    interface TypeBackground {
        neutral: string;
        darker: string;
    }
    interface SimplePaletteColorOptions {
        lightest?: string;
        lighter: string;
        lightSoft?: string;
        darker: string;
        muted?: string;
    }
    interface PaletteColor {
        lightest?: string;
        lighter: string;
        lightSoft?: string;
        darker: string;
        muted?: string;
    }
    interface Palette {
        gradients: GradientsPaletteOptions;
        chart: ChartPaletteOptions;
        insight: PaletteColor;
        rating: PaletteColor;
        tan: PaletteColor;
        tertiary: PaletteColor;
        tertiaryLight: PaletteColor;
        chatBubble: {
            inbound: string;
            outbound: string;
        };
        primaryIndigo: PaletteColor;
        tertiaryBlue: PaletteColor;
        brand: BrandPaletteOptions;
    }
    interface PaletteOptions {
        gradients: GradientsPaletteOptions;
        chart: ChartPaletteOptions;
        insight?: SimplePaletteColorOptions & { contrastText?: string };
        rating?: SimplePaletteColorOptions & { contrastText?: string };
        tan?: SimplePaletteColorOptions & { contrastText?: string };
        tertiary?: SimplePaletteColorOptions & { contrastText?: string };
        tertiaryLight?: SimplePaletteColorOptions & { contrastText?: string };
        chatBubble?: {
            inbound?: string;
            outbound?: string;
        };
        primaryIndigo?: SimplePaletteColorOptions & { contrastText?: string };
        tertiaryBlue?: SimplePaletteColorOptions & { contrastText?: string };
        brand?: BrandPaletteOptions;
    }
}

declare module '@mui/material' {
    interface Color {
        0: string;
        950: string;
        500_8: string;
        500_12: string;
        500_16: string;
        500_24: string;
        500_32: string;
        500_48: string;
        500_56: string;
        500_80: string;
    }
}

// SETUP COLORS
const PRIMARY = {
    lightest: '#E6F0FF',
    lighter: '#E6F0FF',
    light: '#0D50ED',
    main: '#0A3CB1',
    dark: '#08308C',
    darker: '#06246A',
};
const SECONDARY = {
    lighter: '#98C8FB',
    light: '#499EF8',
    main: '#0874E7',
    dark: '#054D99',
    darker: '#03284F',
};
const INFO = {
    lighter: '#D0F2FF',
    light: '#74CAFF',
    main: '#1890FF',
    dark: '#0C53B7',
    darker: '#04297A',
};
const SUCCESS = {
    lighter: '#E9FCD4',
    light: '#AAF27F',
    main: '#4CAF50',
    dark: '#229A16',
    darker: '#08660D',
    muted: '#4D804F',
};
const WARNING = {
    lighter: '#FFF7CD',
    light: '#FFE16A',
    main: '#FB8C00',
    dark: '#B8611A',
    darker: '#7A4F01',
};
const ERROR = {
    lighter: '#FFE6E6',
    light: '#FFA48D',
    main: '#FF4747',
    dark: '#B72136',
    darker: '#730A0A',
};
const INSIGHT = {
    lighter: '#E1F5FE',
    light: '#4FC3F7',
    main: '#03A9F4',
    dark: '#1485B8',
    darker: '#0277BD',
};
const RATING = {
    lightest: '#FFF2E0',
    lighter: '#FED6A8',
    light: '#FDB868',
    main: '#FB8C00',
    dark: '#D97706',
    darker: '#B45309',
};
const TAN = {
    lighter: '#F6F0E6',
    lightSoft: '#F6E6CB',
    light: '#F5D49A',
    main: '#EFE1CA',
    dark: '#A0722A',
    darker: '#6B4718',
};
const TERTIARY = {
    lighter: '#FFF2D9',
    light: '#D9874A',
    main: '#B8611A',
    dark: '#8C4913',
    darker: '#5C300C',
};

const PRIMARY_INDIGO = {
    lightest: '#DCE1FF',
    lighter: '#AEB8FF',
    light: '#7B8AFF',
    main: '#4C5BE0',
    dark: '#3340AD',
    darker: '#1F2A80',
};
const TERTIARY_BLUE = {
    lightest: '#D1E4FF',
    lighter: '#9CC2FF',
    light: '#6BA0FF',
    main: '#3B82F6',
    dark: '#2563C2',
    darker: '#16448C',
};

const TAN_DARK = {
    lighter: alpha(TAN.lighter, 0.16),
    light: '#5C4527',
    main: alpha(TAN.main, 0.16),
    dark: '#F5D49A',
    darker: '#FDF3E3',
};

const GREY = {
    0: '#FFFFFF',
    50: '#F5F5F5',
    100: '#EBEBEB',
    200: '#D9D9D9',
    300: '#DFE3E8',
    400: '#C4CDD5',
    500: '#919EAB',
    600: '#637381',
    700: '#454F5B',
    800: '#212B36',
    900: '#161C24',
    950: '#0A1015',
    500_8: alpha('#919EAB', 0.08),
    500_12: alpha('#919EAB', 0.12),
    500_16: alpha('#919EAB', 0.16),
    500_24: alpha('#919EAB', 0.24),
    500_32: alpha('#919EAB', 0.32),
    500_48: alpha('#919EAB', 0.48),
    500_56: alpha('#919EAB', 0.56),
    500_80: alpha('#919EAB', 0.8),
};

const GRADIENTS = {
    primary: createGradient(PRIMARY.light, PRIMARY.main),
    info: createGradient(INFO.light, INFO.main),
    success: createGradient(SUCCESS.light, SUCCESS.main),
    warning: createGradient(WARNING.light, WARNING.main),
    error: createGradient(ERROR.light, ERROR.main),
    card: createGradient('#EAFFFD', '#D8E2FF', 135),
};

const CHART_COLORS = {
    violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
    blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
    green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
    yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
    red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

export interface AppointmentCalendarColorScheme {
    pageBackground: string;
    surface: string;
    white: string;
    border: string;
    headerBackground: string;
    gridBackground: string;
    textPrimary: string;
    textHeading: string;
    textSecondary: string;
    selectedSegment: string;
    eventBackground: string;
    eventText: string;
    joinButton: string;
    filterIconActive: string;
    filterIconInactive: string;
    currentTime: string;
    danger: string;
    dangerBorder: string;
    chipBackground: string;
    chipText: string;
    divider: string;
    outlinedButtonBorder: string;
    shadow: string;
    statusCards: {
        occurred: {
            background: string;
            text: string;
            border: string;
            overflowBackground: string;
        };
        canceled: {
            background: string;
            text: string;
            border: string;
            overflowBackground: string;
        };
        missed: {
            background: string;
            text: string;
            border: string;
            overflowBackground: string;
        };
        pending: {
            background: string;
            text: string;
            border: string;
            overflowBackground: string;
        };
        confirmed: {
            background: string;
            text: string;
            border: string;
            overflowBackground: string;
        };
    };
}

const APPOINTMENT_CALENDAR_COLORS_LIGHT: AppointmentCalendarColorScheme = {
    pageBackground: '#ffffff',
    surface: '#ffffff',
    white: '#ffffff',
    border: '#c4c4c4',
    headerBackground: '#f5f5f5',
    gridBackground: '#ffffff',
    textPrimary: '#404040',
    textHeading: '#1f1f1f',
    textSecondary: '#808080',
    selectedSegment: '#e6f0ff',
    eventBackground: '#cfddf5',
    eventText: '#205999',
    joinButton: '#0a3cb1',
    filterIconActive: '#0a3cb1',
    filterIconInactive: '#404040',
    currentTime: '#ff4747',
    danger: '#ff4747',
    dangerBorder: '#ff47477a',
    chipBackground: '#0a3cb129',
    chipText: '#205999',
    divider: '#919eab33',
    outlinedButtonBorder: '#9e9e9e52',
    shadow: '#9e9e9e3d',
    statusCards: {
        occurred: {
            background: '#E8FFE9',
            text: '#4D804F',
            border: alpha('#4D804F', 0.18),
            overflowBackground: '#E8FFE9',
        },
        canceled: {
            background: '#FFE6E6',
            text: '#B81A1A',
            border: alpha('#B81A1A', 0.18),
            overflowBackground: '#FFE6E6',
        },
        missed: {
            background: '#FFE7D9',
            text: '#B72136',
            border: alpha('#B72136', 0.18),
            overflowBackground: '#FFE7D9',
        },
        pending: {
            background: '#FFF2D9',
            text: '#B8611A',
            border: alpha('#B8611A', 0.18),
            overflowBackground: '#FFF2D9',
        },
        confirmed: {
            background: '#E6F0FF',
            text: '#205999',
            border: alpha('#205999', 0.18),
            overflowBackground: '#E6F0FF',
        },
    },
};

const APPOINTMENT_CALENDAR_COLORS_DARK: AppointmentCalendarColorScheme = {
    pageBackground: '#161C24',
    surface: '#212B36',
    white: '#ffffff',
    border: '#454F5B',
    headerBackground: '#212B36',
    gridBackground: '#212B36',
    textPrimary: '#F9FAFB',
    textHeading: '#FFFFFF',
    textSecondary: '#919EAB',
    selectedSegment: '#0A3CB129',
    eventBackground: '#1a2a44',
    eventText: '#83CFFF',
    joinButton: '#487BF5',
    filterIconActive: '#83CFFF',
    filterIconInactive: '#F9FAFB',
    currentTime: '#ff4747',
    danger: '#ff4747',
    dangerBorder: alpha('#ff4747', 0.48),
    chipBackground: '#487BF529',
    chipText: '#83CFFF',
    divider: '#919eab33',
    outlinedButtonBorder: '#919eab3d',
    shadow: '#00000066',
    statusCards: {
        occurred: {
            background: '#E8FFE9',
            text: '#4D804F',
            border: alpha('#4D804F', 0.18),
            overflowBackground: '#E8FFE9',
        },
        canceled: {
            background: '#FFE6E6',
            text: '#B81A1A',
            border: alpha('#B81A1A', 0.18),
            overflowBackground: '#FFE6E6',
        },
        missed: {
            background: '#FFE7D9',
            text: '#B72136',
            border: alpha('#B72136', 0.18),
            overflowBackground: '#FFE7D9',
        },
        pending: {
            background: '#FFF2D9',
            text: '#B8611A',
            border: alpha('#B8611A', 0.18),
            overflowBackground: '#FFF2D9',
        },
        confirmed: {
            background: '#E6F0FF',
            text: '#205999',
            border: alpha('#205999', 0.18),
            overflowBackground: '#E6F0FF',
        },
    },
};

export const APPOINTMENT_CALENDAR_COLORS: AppointmentCalendarColorScheme =
    APPOINTMENT_CALENDAR_COLORS_LIGHT;

export const getAppointmentCalendarColors = (
    mode: 'light' | 'dark'
): AppointmentCalendarColorScheme =>
    mode === 'dark' ? APPOINTMENT_CALENDAR_COLORS_DARK : APPOINTMENT_CALENDAR_COLORS_LIGHT;

export type AssessmentSeverityKey =
    | 'no'
    | 'minimal'
    | 'mild'
    | 'moderate'
    | 'moderately severe'
    | 'severe';

export interface AssessmentSeverityColor {
    bgColor: string;
    fontColor: string;
    borderColor: string;
}

export type AssessmentSeverityColorScheme = Record<AssessmentSeverityKey, AssessmentSeverityColor>;

// Severity swatches are tuned so fontColor on bgColor clears WCAG 2.1 AA (>= 4.5:1)
// for normal-size text. Ratios are noted per entry; keep them above 4.5 when editing.
const ASSESSMENT_SEVERITY_COLORS_LIGHT: AssessmentSeverityColorScheme = {
    no: { bgColor: '#E6F6E9', fontColor: '#1B5E20', borderColor: alpha('#1B5E20', 0.24) }, // 6.9:1
    minimal: { bgColor: '#EDF7E0', fontColor: '#33691E', borderColor: alpha('#33691E', 0.24) }, // 5.9:1
    mild: { bgColor: '#FFF8DB', fontColor: '#7A5B00', borderColor: alpha('#7A5B00', 0.24) }, // 5.8:1
    moderate: { bgColor: '#FFEEDB', fontColor: '#8A4B00', borderColor: alpha('#8A4B00', 0.24) }, // 6.0:1
    'moderately severe': {
        bgColor: '#FFE6E6',
        fontColor: '#A31212',
        borderColor: alpha('#A31212', 0.24),
    }, // 6.6:1
    severe: { bgColor: '#F1E6FA', fontColor: '#5B2A86', borderColor: alpha('#5B2A86', 0.24) }, // 8.2:1
};

const ASSESSMENT_SEVERITY_COLORS_DARK: AssessmentSeverityColorScheme = {
    no: { bgColor: '#1B3A21', fontColor: '#A5E0B0', borderColor: alpha('#A5E0B0', 0.24) }, // 8.5:1
    minimal: { bgColor: '#24361A', fontColor: '#C3E59A', borderColor: alpha('#C3E59A', 0.24) }, // 9.1:1
    mild: { bgColor: '#3A3316', fontColor: '#F0D373', borderColor: alpha('#F0D373', 0.24) }, // 8.6:1
    moderate: { bgColor: '#3D2A14', fontColor: '#FFC98A', borderColor: alpha('#FFC98A', 0.24) }, // 9.1:1
    'moderately severe': {
        bgColor: '#3E1E1E',
        fontColor: '#FFA3A3',
        borderColor: alpha('#FFA3A3', 0.24),
    }, // 7.8:1
    severe: { bgColor: '#2F1F45', fontColor: '#D0AEFF', borderColor: alpha('#D0AEFF', 0.24) }, // 7.9:1
};

export const getAssessmentSeverityColors = (
    mode: 'light' | 'dark'
): AssessmentSeverityColorScheme =>
    mode === 'dark' ? ASSESSMENT_SEVERITY_COLORS_DARK : ASSESSMENT_SEVERITY_COLORS_LIGHT;

export interface AssessmentCardColor {
    bgColor: string;
    fontColor: string;
    secondaryFontColor: string;
    borderColor: string;
}

export interface AssessmentCardColorScheme {
    default: AssessmentCardColor;
    disabled: AssessmentCardColor;
    byType: Partial<Record<AssessmentType, AssessmentCardColor>>;
}

// Assessment library card tints. Both fontColor and secondaryFontColor clear WCAG 2.1 AA
// (>= 4.5:1) against bgColor; the card copy is 14-20px, so the large-text 3:1 allowance
// does not apply. Ratios are noted as fontColor / secondaryFontColor.
const ASSESSMENT_CARD_COLORS_LIGHT: AssessmentCardColorScheme = {
    default: {
        bgColor: '#EEF1F5',
        fontColor: '#1F2933',
        secondaryFontColor: '#52606D',
        borderColor: alpha('#1F2933', 0.12),
    }, // 13.5 / 5.7
    disabled: {
        bgColor: GREY[100],
        fontColor: '#454F5B',
        secondaryFontColor: '#52606D',
        borderColor: alpha('#454F5B', 0.16),
    }, // 7.0 / 5.5
    byType: {
        [AssessmentType.PHQ9]: {
            bgColor: '#DCE7FA',
            fontColor: '#123C7A',
            secondaryFontColor: '#3E5C87',
            borderColor: alpha('#123C7A', 0.16),
        }, // 8.8 / 5.5
        [AssessmentType.GAD7]: {
            bgColor: '#DCF0DE',
            fontColor: '#14532D',
            secondaryFontColor: '#3B6B47',
            borderColor: alpha('#14532D', 0.16),
        }, // 7.8 / 5.2
        [AssessmentType.AUDIT_C]: {
            bgColor: '#FFE0D0',
            fontColor: '#7A3110',
            secondaryFontColor: '#8A4B2A',
            borderColor: alpha('#7A3110', 0.16),
        }, // 7.5 / 5.5
        [AssessmentType.DAST_10]: {
            bgColor: '#F8DAEC',
            fontColor: '#7A1A52',
            secondaryFontColor: '#8A3A66',
            borderColor: alpha('#7A1A52', 0.16),
        }, // 7.8 / 5.7
        [AssessmentType.SIP_AD_5]: {
            bgColor: '#F0C8D7',
            fontColor: '#6B1030',
            secondaryFontColor: '#7A2543',
            borderColor: alpha('#6B1030', 0.16),
        }, // 8.0 / 6.4
        [AssessmentType.VC_FEEDBACK]: {
            bgColor: '#CFECE7',
            fontColor: '#0B4F46',
            secondaryFontColor: '#2F6A62',
            borderColor: alpha('#0B4F46', 0.16),
        }, // 7.6 / 5.0
        [AssessmentType.PROVIDER_REVIEW]: {
            bgColor: PRIMARY_INDIGO.lightest,
            fontColor: PRIMARY_INDIGO.darker,
            secondaryFontColor: PRIMARY_INDIGO.dark,
            borderColor: alpha(PRIMARY_INDIGO.darker, 0.16),
        }, // 9.6 / 6.5
    },
};

const ASSESSMENT_CARD_COLORS_DARK: AssessmentCardColorScheme = {
    default: {
        bgColor: '#252D38',
        fontColor: '#E4E9EF',
        secondaryFontColor: '#A7B2BF',
        borderColor: alpha('#E4E9EF', 0.16),
    }, // 11.9 / 6.0
    disabled: {
        bgColor: '#2A323C',
        fontColor: GREY[400],
        secondaryFontColor: GREY[500],
        borderColor: alpha(GREY[400], 0.16),
    }, // 8.2 / 4.8
    byType: {
        [AssessmentType.PHQ9]: {
            bgColor: '#16233D',
            fontColor: '#C9DAF7',
            secondaryFontColor: '#9FB6DC',
            borderColor: alpha('#C9DAF7', 0.16),
        }, // 11.1 / 7.6
        [AssessmentType.GAD7]: {
            bgColor: '#14291A',
            fontColor: '#BFE6C6',
            secondaryFontColor: '#9ACBA4',
            borderColor: alpha('#BFE6C6', 0.16),
        }, // 11.3 / 8.4
        [AssessmentType.AUDIT_C]: {
            bgColor: '#33200F',
            fontColor: '#FFD3B8',
            secondaryFontColor: '#E0AE8A',
            borderColor: alpha('#FFD3B8', 0.16),
        }, // 11.3 / 7.9
        [AssessmentType.DAST_10]: {
            bgColor: '#331A28',
            fontColor: '#F5C2DE',
            secondaryFontColor: '#D79DBF',
            borderColor: alpha('#F5C2DE', 0.16),
        }, // 10.4 / 7.2
        [AssessmentType.SIP_AD_5]: {
            bgColor: '#331722',
            fontColor: '#F4B9CE',
            secondaryFontColor: '#D894AC',
            borderColor: alpha('#F4B9CE', 0.16),
        }, // 9.9 / 6.7
        [AssessmentType.VC_FEEDBACK]: {
            bgColor: '#102C29',
            fontColor: '#A9DDD5',
            secondaryFontColor: '#7FBDB4',
            borderColor: alpha('#A9DDD5', 0.16),
        }, // 10.0 / 6.9
        [AssessmentType.PROVIDER_REVIEW]: {
            bgColor: '#1A1F3D',
            fontColor: '#C8CEFF',
            secondaryFontColor: PRIMARY_INDIGO.lighter,
            borderColor: alpha('#C8CEFF', 0.16),
        }, // 10.5 / 8.5
    },
};

export const getAssessmentCardColors = (mode: 'light' | 'dark'): AssessmentCardColorScheme =>
    mode === 'dark' ? ASSESSMENT_CARD_COLORS_DARK : ASSESSMENT_CARD_COLORS_LIGHT;

export const TEXT = {
    light: {
        primary: '#404040',
        secondary: '#808080',
    },
    dark: {
        primary: GREY[0],
        secondary: GREY[500],
    },
};

const COMMON = {
    common: { black: '#000', white: '#fff' },
    primary: { ...PRIMARY, contrastText: '#fff' },
    secondary: { ...SECONDARY, contrastText: '#fff' },
    info: { ...INFO, contrastText: '#fff' },
    success: { ...SUCCESS, contrastText: GREY[800] },
    warning: { ...WARNING, contrastText: GREY[800] },
    error: { ...ERROR, contrastText: '#fff' },
    insight: { ...INSIGHT, contrastText: '#fff' },
    rating: { ...RATING, contrastText: '#fff' },
    tan: { ...TAN, contrastText: '#fff' },
    tertiary: { ...TAN, contrastText: '#fff' },
    tertiaryLight: { ...TAN, contrastText: '#fff' },
    primaryIndigo: { ...PRIMARY_INDIGO, contrastText: '#fff' },
    tertiaryBlue: { ...TERTIARY_BLUE, contrastText: '#fff' },
    brand: { blue: '#42B5E7' },
    grey: GREY,
    gradients: GRADIENTS,
    chart: CHART_COLORS,
    divider: GREY[500_24],
    action: {
        hover: GREY[500_8],
        selected: GREY[500_16],
        disabled: GREY[500_80],
        disabledBackground: GREY[500_24],
        focus: GREY[500_24],
        hoverOpacity: 0.08,
        disabledOpacity: 0.48,
    },
    global: {
        default: {
            bgColor: '#C4C4C4',
        },
    },
};

const light = {
    ...COMMON,
    mode: 'light',
    text: {
        primary: TEXT.light.primary,
        secondary: TEXT.light.secondary,
        disabled: '#9E9E9E',
        dark: TEXT.dark,
    },
    tertiaryLight: { ...TAN, contrastText: TAN.darker },
    tan: { ...TAN, contrastText: TAN.darker },
    tertiary: { ...TAN, contrastText: TAN.darker },
    background: { paper: GREY[0], default: GREY[50], neutral: GREY[100], darker: GREY[200] },
    primaryIndigo: { ...PRIMARY_INDIGO, contrastText: PRIMARY_INDIGO.darker },
    tertiaryBlue: { ...TERTIARY_BLUE, contrastText: TERTIARY_BLUE.darker },
    action: { active: '#808080', ...COMMON.action },
    chatBubble: {
        inbound: GREY[100],
        outbound: PRIMARY.lightest,
    },
} as const;

const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
        primary: TEXT.dark.primary,
        secondary: TEXT.dark.secondary,
        disabled: GREY[600],
        light: TEXT.light,
    },
    tertiaryLight: { ...TAN_DARK, contrastText: '#fff' },
    tan: { ...TAN_DARK, contrastText: '#fff' },
    tertiary: { ...TAN_DARK, contrastText: '#fff' },
    primaryIndigo: { ...PRIMARY_INDIGO, contrastText: '#fff' },
    tertiaryBlue: { ...TERTIARY_BLUE, contrastText: '#fff' },
    background: { paper: GREY[800], default: GREY[900], neutral: GREY[500_16] },
    action: { active: GREY[500], ...COMMON.action },
    chatBubble: {
        inbound: GREY[950],
        outbound: PRIMARY.dark,
    },
} as const;

const warm = {
    ...light,
    background: { ...light.background, default: '#FEFBF6' },
    chatBubble: {
        inbound: '#F6F0E6',
        outbound: PRIMARY.lightest,
    },
} as const;

const palette = {
    dark,
    light,
    warm,
} as const;

export default palette;
